// Registers <sb-ingredients-modal> custom element and uses TD_PUBLISH/TD_SUBSCRIBE

if (!customElements.get("sb-ingredients-modal")) {
  class SbIngredientsModal extends HTMLElement {
    connectedCallback() {
      this.initializeInstanceVariables();
      this.setupEventListeners();
    }

    initializeInstanceVariables() {
      this.isOpen = false;
      this.isClosing = false;
      this.isLoading = false;
      this.hasError = false;
      /** @type {string|null} */ this.cachedContent = null;
      /** @type {string|null} */ this.lastFetchUrl = null;
      /** @type {string|null} */ this.currentSectionUrl = null;
      /** @type {HTMLElement|null} */ this.lastTrigger = null;
      this.backdrop = this.querySelector("[data-backdrop]");
      this.modalContainer = this.querySelector("[data-container]");
      this.modalContent = this.querySelector("[data-content]");
      this.closeButton = this.querySelector("[data-close-button]");
      this.isFullScreen = this.dataset.isFullScreen !== "false";
      this.loadingTemplate = /** @type {HTMLElement|null} */ (
        this.querySelector('[data-template="loading"]')
      );
      this.errorTemplate = /** @type {HTMLElement|null} */ (
        this.querySelector('[data-template="error"]')
      );
      this.modalId = this.dataset.modalId
        ? String(this.dataset.modalId)
        : "td-modal";
      this.closeOnBackdrop = this.dataset.closeOnBackdrop !== "false";
      this.closeOnEscape = this.dataset.closeOnEscape !== "false";
      this.showCloseButton = this.dataset.showCloseButton !== "false";
      this.cacheContent = this.dataset.cacheContent !== "false";
      this.showLoading = this.dataset.showLoading !== "false";
      /** @type {number} */ this.retryAttempts = this.dataset.retryAttempts
        ? parseInt(this.dataset.retryAttempts)
        : 3;
      this.staticContent = this.modalContent ? this.modalContent.innerHTML : "";
    }

    setupEventListeners() {
      this.setupModalEventListeners();
      this.setupGlobalEventListeners();
      this.setupModalInteractionListeners();
    }

    setupModalEventListeners() {
      this.openSubscriber = TD_SUBSCRIBE(
        `${this.modalId}:open`,
        this.handleOpen.bind(this)
      );
      this.closeSubscriber = TD_SUBSCRIBE(
        `${this.modalId}:close`,
        this.handleClose.bind(this)
      );
      this.refreshSubscriber = TD_SUBSCRIBE(
        `${this.modalId}:refresh`,
        this.handleRefreshContent.bind(this)
      );
    }

    setupGlobalEventListeners() {
      if (this.closeOnEscape) {
        this.escapeListener = this.handleEscapeKey.bind(this);
        document.addEventListener("keydown", this.escapeListener);
      }
      this.resizeListener = this.handleWindowResize.bind(this);
      window.addEventListener("resize", this.resizeListener);
    }

    setupModalInteractionListeners() {
      if (this.closeOnBackdrop && this.backdrop) {
        this.backdrop.addEventListener(
          "click",
          this.handleBackdropClick.bind(this)
        );
      }
      if (this.showCloseButton && this.closeButton) {
        this.closeButton.addEventListener(
          "click",
          this.handleCloseButtonClick.bind(this)
        );
      }
      if (this.modalContainer) {
        this.modalContainer.addEventListener(
          "click",
          this.handleModalClick.bind(this)
        );
      }
    }

    /** @param {{sectionUrl?: string}=} eventData */
    async handleOpen(eventData = {}) {
      if (this.isOpen || this.isClosing) return;
      this.isOpen = true;
      this.isClosing = false;
      this.classList.remove("td-modal--closing");
      this.classList.add("td-modal--open");
      this.lockBodyScroll();
      // Move focus inside the dialog (prefer close button, then container)
      const focusTarget = this.closeButton || this.modalContainer;
      if (focusTarget) {
        requestAnimationFrame(() =>
          /** @type {HTMLElement} */ (focusTarget).focus()
        );
      }
      if (eventData.sectionUrl) {
        this.currentSectionUrl = eventData.sectionUrl;
      }
      if (this.currentSectionUrl) {
        await this.fetchAndUpdateContent();
      }
      TD_PUBLISH(`${this.modalId}:opened`, { element: this });
    }

    /** @param {number} [attemptCount=0] @returns {Promise<boolean>} */
    async fetchAndUpdateContent(attemptCount = 0) {
      if (!this.currentSectionUrl) return false;
      if (
        this.cacheContent &&
        this.cachedContent &&
        this.lastFetchUrl === this.currentSectionUrl
      ) {
        this.updateModalContent(this.cachedContent);
        return true;
      }
      try {
        this.setLoadingState(true);
        this.hasError = false;
        const response = await fetch(this.currentSectionUrl, {
          method: "GET",
          headers: {
            Accept: "text/html",
            "X-Requested-With": "XMLHttpRequest",
          },
        });
        if (!response.ok)
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const htmlContent = await response.text();
        if (this.cacheContent) {
          this.cachedContent = htmlContent;
          this.lastFetchUrl = this.currentSectionUrl;
        }
        this.updateModalContent(htmlContent);
        this.setLoadingState(false);
        TD_PUBLISH(`${this.modalId}:contentLoaded`, {
          element: this,
          content: htmlContent,
        });
        return true;
      } catch (error) {
        if (
          typeof attemptCount === "number" &&
          attemptCount < Number(this.retryAttempts)
        ) {
          const delay = Math.pow(2, attemptCount) * 1000;
          await new Promise((r) => setTimeout(r, delay));
          return await this.fetchAndUpdateContent(attemptCount + 1);
        }
        this.handleFetchError(error);
        return false;
      }
    }

    /** @param {Error} error */
    handleFetchError(error) {
      this.hasError = true;
      this.setLoadingState(false);
      const errorContent = this.getErrorTemplate(error.message);
      this.updateModalContent(errorContent);
      TD_PUBLISH(`${this.modalId}:contentError`, { element: this, error });
    }

    /** @param {string} errorMessage */
    getErrorTemplate(errorMessage) {
      if (!this.errorTemplate) {
        return `<div class="td-modal__error"><div class="td-modal__error-icon">⚠️</div><h3 class="td-modal__error-title">Failed to Load Content</h3><p class="td-modal__error-message">${errorMessage}</p><button type="button" class="td-modal__retry-btn" data-retry-action>Try Again</button></div>`;
      }
      const errorClone = /** @type {HTMLElement} */ (
        this.errorTemplate.cloneNode(true)
      );
      const errorMessageElement = errorClone.querySelector(
        "[data-error-message]"
      );
      if (errorMessageElement) errorMessageElement.textContent = errorMessage;
      return errorClone.outerHTML;
    }

    getLoadingTemplate() {
      if (!this.loadingTemplate) {
        return `<div class="td-modal__loading"><div class="td-modal__spinner"><svg class="td-modal__spinner-icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-dasharray="32" stroke-dashoffset="32"><animate attributeName="stroke-dashoffset" dur="1s" values="32;0" repeatCount="indefinite"/></circle></svg></div><p class="td-modal__loading-text">Loading content...</p></div>`;
      }
      return this.loadingTemplate.outerHTML;
    }

    /** @param {boolean} isLoading */
    setLoadingState(isLoading) {
      this.isLoading = isLoading;
      if (isLoading) {
        this.classList.add("td-modal--loading");
        if (this.showLoading)
          this.updateModalContent(this.getLoadingTemplate());
      } else {
        this.classList.remove("td-modal--loading");
      }
    }

    /** @param {string} htmlContent */
    updateModalContent(htmlContent) {
      if (!this.modalContent) return;
      this.modalContent.innerHTML = htmlContent;
      const retryButton = this.modalContent.querySelector(
        "[data-retry-button], [data-retry-action]"
      );
      if (retryButton)
        retryButton.addEventListener("click", this.handleRetry.bind(this));
      TD_PUBLISH(`${this.modalId}:contentUpdated`, { element: this });
    }

    async handleRetry() {
      if (this.currentSectionUrl && !this.isLoading) {
        this.cachedContent = null;
        this.lastFetchUrl = null;
        await this.fetchAndUpdateContent();
      }
    }

    /** @param {{sectionUrl?: string}=} eventData */
    async handleRefreshContent(eventData = {}) {
      if (eventData.sectionUrl) this.currentSectionUrl = eventData.sectionUrl;
      this.cachedContent = null;
      this.lastFetchUrl = null;
      if (!this.isOpen && !this.isClosing) {
        await this.handleOpen(eventData);
      } else if (this.currentSectionUrl) {
        await this.fetchAndUpdateContent();
      }
    }

    handleClose() {
      if (!this.isOpen || this.isClosing) return;
      this.isClosing = true;
      this.classList.add("td-modal--closing");
      setTimeout(() => {
        this.isOpen = false;
        this.isClosing = false;
        this.classList.remove(
          "td-modal--open",
          "td-modal--closing",
          "td-modal--loading"
        );
        this.unlockBodyScroll();
        // Return focus to last trigger if available
        if (this.lastTrigger && typeof this.lastTrigger.focus === "function") {
          this.lastTrigger.focus();
        }
        if (this.currentSectionUrl && this.staticContent)
          this.updateModalContent(this.staticContent);
        this.currentSectionUrl = null;
        TD_PUBLISH(`${this.modalId}:closed`, { element: this });
      }, 300);
    }

    /** @param {KeyboardEvent} event */
    handleEscapeKey(event) {
      if (event.key === "Escape" && this.isOpen && !this.isClosing)
        this.handleClose();
    }
    /** @param {MouseEvent} event */
    handleBackdropClick(event) {
      if (event.target === this.backdrop && !this.isClosing) this.handleClose();
    }
    /** @param {MouseEvent} event */
    handleCloseButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.isClosing) this.handleClose();
    }
    /** @param {MouseEvent} event */
    handleModalClick(event) {
      event.stopPropagation();
    }

    lockBodyScroll() {
      document.body.style.overflow = "hidden";
      document.body.classList.add("td-modal-open");
    }
    unlockBodyScroll() {
      document.body.style.overflow = "";
      document.body.classList.remove("td-modal-open");
    }
    handleWindowResize() {
      // no-op
    }
    disconnectedCallback() {
      if (this.openSubscriber) this.openSubscriber();
      if (this.closeSubscriber) this.closeSubscriber();
      if (this.refreshSubscriber) this.refreshSubscriber();
      if (this.escapeListener)
        document.removeEventListener("keydown", this.escapeListener);
      if (this.resizeListener)
        window.removeEventListener("resize", this.resizeListener);
      if (this.isOpen) this.unlockBodyScroll();
    }

    open() {
      this.handleOpen();
    }
    close() {
      this.handleClose();
    }
  }
  customElements.define("sb-ingredients-modal", SbIngredientsModal);
}
